import { useScreenSize } from '@insight-health-ai/roseate/utils';
import {
  InfoSquare,
  Flag06,
  UserRight01,
  UserLeft01,
  ImageUser,
  CalendarDate,
  CalendarPlus01,
  ChevronLeft,
  MessageNotificationSquare,
  Mail01,
  ChevronRight,
} from '@insight-health-ai/roseate/icons';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { usePageHeader } from 'src/hooks/misc';
import {
  ChatHistoryCard,
  CopyToClipboard,
  ServiceRequestStatus,
  SummaryCard,
  EditableField,
} from 'src/components';
import {
  ScreenLoader,
  DetailsBar,
  DetailsBarProps,
  InsightTag,
} from 'src/components';
import { TagSelector } from 'src/components/tag';
import {
  useGetServiceRequestDetails,
  useGetServiceRequestDocumentList,
  useTagServiceRequest,
  useUntagServiceRequest,
  useUpdatePatientContactDetails,
} from 'src/hooks/serviceRequest';
import { Button, Card, Segmented } from '@insight-health-ai/roseate/components';
import { formatStaticDate } from 'src/utils/date';
import {
  EncounterPhase,
  EncounterSummaryType,
  ServiceRequest,
} from 'src/models';
import { useEffect, useMemo, useState } from 'react';
import PDFRenderer from 'src/components/pdfRenderer';
import { getPractitionerName } from 'src/utils';
import { Select, Tooltip } from 'antd';

type ReferralDetailsProps = {
  referralId: string;
};

export const ReferralDetails = ({ referralId }: ReferralDetailsProps) => {
  const navigate = useNavigate();
  const { mediaMax } = useScreenSize();
  const [activeDocumentIndex, setActiveDocumentIndex] = useState<number>(0);
  const { data: referral, isLoading: isReferralLoading } =
    useGetServiceRequestDetails(
      {
        serviceRequestId: referralId,
      },
      {
        refetchInterval: (query) => {
          if (
            (query.state.data?.status?.status_name || '')?.toLowerCase() ===
            'processing'
          ) {
            return 5000;
          }
          return false;
        },
      }
    );

  const { mutateAsync: tagServiceRequest } = useTagServiceRequest();
  const { mutateAsync: untagServiceRequest } = useUntagServiceRequest();
  const { mutateAsync: updatePatientContactDetails } =
    useUpdatePatientContactDetails(referralId);

  const { data: documentData, isLoading: isDocumentLoading } =
    useGetServiceRequestDocumentList(
      {
        serviceRequestId: referralId,
        includePresignedUrl: true,
      },
      {
        refetchOnWindowFocus: false,
      }
    );

  const [activeTab, setActiveTab] = useState<'encounter' | 'documents'>(
    'encounter'
  );
  const tabs = useMemo(() => {
    const newTabs = [];
    if (referral?.encounter) {
      newTabs.push({
        label: 'Encounter',
        value: 'encounter' as typeof activeTab,
        content: (
          <div className="flex flex-col md:flex-row gap-3 h-full w-full overflow-hidden">
            <SummaryCard
              filters={{
                encounterId: referral.encounter.encounter_id || '',
                encounterPhase: referral.encounter.type as EncounterPhase,
                type: referral.encounter.type as EncounterSummaryType,
              }}
              encounter={referral.encounter}
            />
            <ChatHistoryCard
              filters={{
                encounterId: referral.encounter.encounter_id || '',
              }}
              patient={referral.patient ?? undefined}
            />
          </div>
        ),
      });
    }

    if (documentData?.documents.length) {
      newTabs.push({
        label: 'Documents',
        value: 'documents' as typeof activeTab,
        content: (
          <div className="flex flex-col md:flex-row gap-3 h-full w-full overflow-hidden">
            <div className="h-full flex flex-col flex-shrink-0 w-full md:w-[40%] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-2 p-2 bg-gray-100">
                <Tooltip title="Previous document">
                  <Button
                    variant="text"
                    appearance="secondary"
                    icon={<ChevronLeft />}
                    disabled={activeDocumentIndex <= 0}
                    onClick={() =>
                      setActiveDocumentIndex(
                        Math.max(activeDocumentIndex - 1, 0)
                      )
                    }
                  />
                </Tooltip>
                <Select
                  variant="borderless"
                  options={documentData.documents.map((doc, index) => ({
                    label: doc.name,
                    value: index,
                  }))}
                  value={activeDocumentIndex}
                  onChange={(value) => setActiveDocumentIndex(Number(value))}
                />
                <Tooltip title="Next document">
                  <Button
                    variant="text"
                    appearance="secondary"
                    icon={<ChevronRight />}
                    disabled={
                      activeDocumentIndex >= documentData.documents.length - 1
                    }
                    onClick={() =>
                      setActiveDocumentIndex(
                        Math.min(
                          activeDocumentIndex + 1,
                          documentData.documents.length - 1
                        )
                      )
                    }
                  />
                </Tooltip>
              </div>
              <PDFRenderer
                url={
                  documentData.documents[activeDocumentIndex].presigned_url ??
                  ''
                }
              />
            </div>
            <SummaryCard
              editable={true}
              filters={{
                serviceRequestId: referralId,
                type: 'service-request-documents',
              }}
              emptyPlaceholder="The documents are being processed. Please check back shortly."
            />
          </div>
        ),
      });
    }
    return newTabs;
  }, [documentData?.documents, referral, mediaMax, activeDocumentIndex]);

  const isLoading = isReferralLoading || (!tabs.length && isDocumentLoading);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (tabs.length && !tabs.find((tab) => tab.value === activeTab)) {
      setActiveTab(tabs[0]?.value);
    }
  }, [activeTab, tabs]);

  if (isLoading) {
    return <ScreenLoader />;
  }

  if (!referral) {
    navigate('/referrals');
    return null;
  }

  const content = tabs.find((tab) => tab.value === activeTab)?.content;

  // TODO: add error handling

  const getPatientContactFields = (referral: ServiceRequest) => {
    if (!referral.patient_id || !referral.patient) return [];

    return [
      {
        title: 'Phone number',
        content: referral.patient?.phone ? (
          <EditableField
            text={String(referral.patient.phone?.phone_text)}
            onSave={(value: string) => {
              updatePatientContactDetails({
                patientId: referral.patient_id,
                phone: (value || '').trim(),
              });
            }}
          />
        ) : null,
        icon: <MessageNotificationSquare />,
        fallback: 'Unknown',
      },
      {
        title: 'Email',
        content: referral.patient?.email ? (
          <EditableField
            text={String(referral.patient.email?.email_text)}
            onSave={(value: string) => {
              updatePatientContactDetails({
                patientId: referral.patient_id,
                email: (value || '').trim(),
              });
            }}
          />
        ) : null,
        icon: <Mail01 />,
        fallback: 'Unknown',
      },
    ];
  };

  const referralDetails: DetailsBarProps['details'] = [
    [
      {
        title: 'Assigned To',
        content:
          referral.performing_practitioner &&
          getPractitionerName(referral.performing_practitioner),
        icon: <UserLeft01 />,
        fallback: 'Unknown',
      },
      {
        title: 'Referral Date',
        content: referral.system_created_date
          ? formatStaticDate(referral.system_created_date, 'MMM DD, YYYY')
          : null,
        icon: <CalendarPlus01 />,
        fallback: 'Unknown',
      },
      {
        title: 'Referral Source',
        content: referral.requesting_practitioner
          ? getPractitionerName(referral.requesting_practitioner)
          : null,
        icon: <UserRight01 />,
        fallback: 'Unknown',
      },
      {
        title: 'Status',
        content: (
          <ServiceRequestStatus
            status={referral.status}
            statusProps={{ size: 'xs' }}
          />
        ),
        icon: <Flag06 />,
        fallback: 'Unknown',
        style: {
          textTransform: 'capitalize',
        },
      },
    ],
    [
      {
        title: 'Patient',
        content:
          referral.patient &&
          [referral.patient.first_name, referral.patient.last_name]
            .filter(Boolean)
            .join(' '),
        icon: <ImageUser />,
        fallback: 'Unknown',
      },
      {
        title: 'Date of birth',
        content: referral.patient?.birth_date
          ? formatStaticDate(referral.patient.birth_date, 'MM/DD/YYYY')
          : null,
        icon: <CalendarDate />,
        fallback: 'Unknown',
      },
      {
        title: 'ID',
        content: referral.patient?.patient_id ? (
          <CopyToClipboard text={String(referral.patient.patient_id)} />
        ) : null,
        icon: <InfoSquare />,
        fallback: 'Unknown',
      },
      referral.patient?.ehr_id && {
        title: 'EHR ID',
        content: referral.patient?.ehr_id ? (
          <CopyToClipboard text={String(referral.patient.ehr_id)} />
        ) : null,
        icon: <InfoSquare />,
        fallback: 'Unknown',
      },
      ...getPatientContactFields(referral),
    ].filter(Boolean) as DetailsBarProps['details'][number],
  ];

  return (
    <div className="md:h-full overflow-hidden">
      <div className="flex flex-col gap-3 h-full overflow-hidden">
        <DetailsBar details={referralDetails} />
        {!!referral.tags?.length && (
          <Card outlined={false} padding={'sm'}>
            <div className="flex gap-2">
              <div className="flex flex-wrap gap-2">
                {referral.tags.map((tag) => (
                  <InsightTag
                    key={tag.id}
                    tag={tag}
                    isSelected
                    onClose={async () => {
                      await untagServiceRequest({
                        serviceRequestId: referral.service_request_id,
                        tag,
                      });
                    }}
                  />
                ))}
                <TagSelector
                  selectedTags={referral.tags}
                  onAdd={async (tag) => {
                    await tagServiceRequest({
                      serviceRequestId: referral.service_request_id,
                      tag,
                    });
                  }}
                  onRemove={async (tag) => {
                    await untagServiceRequest({
                      serviceRequestId: referral.service_request_id,
                      tag,
                    });
                  }}
                />
              </div>
            </div>
          </Card>
        )}
        <div className="flex flex-col items-start gap-3 w-full h-full overflow-hidden">
          {tabs.length > 1 && (
            <Segmented
              options={tabs}
              value={activeTab}
              onChange={(value) => setActiveTab(value as typeof activeTab)}
            />
          )}
          {content}
        </div>
      </div>
    </div>
  );
};

export const ReferralDetailsRoute = () => {
  const { referralId } = useParams();

  if (!referralId) {
    return <Navigate to="/referrals" />;
  }

  usePageHeader({
    title: 'Request details',
    breadcrumb: [
      {
        icon: <ChevronLeft />,
        title: 'Back to referrals',
        href: '/referrals',
      },
    ],
  });

  const { mediaMin } = useScreenSize();

  return (
    <div
      style={mediaMin('md') ? { height: 'calc(100dvh - 100px)' } : {}}
      className="page-wrapper care-connect-page overflow-hidden"
    >
      <ReferralDetails referralId={referralId} />
    </div>
  );
};
